// ============================================================
// OSHA General Industry Compliance Audit Tool
// Main App — navigation, state, save/load wiring
// ============================================================

import React, { useState } from "react";
import { AUDIT_CATEGORIES, DEFAULT_COMPLIANCE } from "./data/auditCriteria";
import { saveAudit, loadAudit } from "./utils/auditStorage";
import AuditSetup from "./components/AuditSetup";
import CategorySelector from "./components/CategorySelector";
import AuditForm from "./components/AuditForm";
import Dashboard from "./components/Dashboard";
import FindingsReport from "./components/FindingsReport";
import AuditManager from "./components/AuditManager";
import "./App.css";

const STEPS = {
  SETUP: "setup",
  SELECT: "select",
  AUDIT: "audit",
  DASHBOARD: "dashboard",
  FINDINGS: "findings",
};

const BLANK_AUDIT_INFO = {
  companyName: "",
  facilityName: "",
  facilityAddress: "",
  auditDate: new Date().toISOString().split("T")[0],
  auditorName: "",
  auditType: "Comprehensive",
  scope: "",
  industry: "",
  employeeCount: "",
  nextAuditDate: "",
};

export default function App() {
  const [step, setStep] = useState(STEPS.SETUP);
  const [auditInfo, setAuditInfo] = useState(BLANK_AUDIT_INFO);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [responses, setResponses] = useState({});
  const [currentAuditId, setCurrentAuditId] = useState(null);
  const [showManager, setShowManager] = useState(false);
  const [isDirty, setIsDirty] = useState(false); // unsaved changes flag

  // ── Initialize blank responses for newly selected categories ──
  const initializeResponses = (cats, existingResponses = {}) => {
    const init = { ...existingResponses };
    cats.forEach((catId) => {
      const cat = AUDIT_CATEGORIES.find((c) => c.id === catId);
      if (cat) {
        cat.criteria.forEach((crit) => {
          if (!init[crit.id]) {
            init[crit.id] = { ...DEFAULT_COMPLIANCE };
          }
        });
      }
    });
    return init;
  };

  // ── Step handlers ──
  const handleSetupComplete = (info) => {
    setAuditInfo(info);
    setIsDirty(true);
    setStep(STEPS.SELECT);
  };

  const handleCategorySelection = (cats) => {
    setSelectedCategories(cats);
    setResponses((prev) => initializeResponses(cats, prev));
    setIsDirty(true);
    setStep(STEPS.AUDIT);
  };

  const handleResponseChange = (criterionId, field, value) => {
    setResponses((prev) => ({
      ...prev,
      [criterionId]: { ...prev[criterionId], [field]: value },
    }));
    setIsDirty(true);
  };

  // ── Save / Load / New ──
  const handleSave = () => {
    const id = saveAudit(currentAuditId, auditInfo, selectedCategories, responses);
    setCurrentAuditId(id);
    setIsDirty(false);
  };

  const handleLoad = (auditMeta) => {
    const data = loadAudit(auditMeta.id);
    if (!data) return;
    setAuditInfo(data.auditInfo);
    setSelectedCategories(data.selectedCategories);
    setResponses(data.responses);
    setCurrentAuditId(data.id);
    setIsDirty(false);
    setStep(STEPS.DASHBOARD);
  };

  const handleNew = () => {
    setAuditInfo({ ...BLANK_AUDIT_INFO, auditDate: new Date().toISOString().split("T")[0] });
    setSelectedCategories([]);
    setResponses({});
    setCurrentAuditId(null);
    setIsDirty(false);
    setStep(STEPS.SETUP);
  };

  // ── Derived data ──
  const selectedCriteria = AUDIT_CATEGORIES.filter((cat) =>
    selectedCategories.includes(cat.id)
  );

  const stepOrder = [STEPS.SETUP, STEPS.SELECT, STEPS.AUDIT, STEPS.DASHBOARD, STEPS.FINDINGS];
  const isAccessible = (target) =>
    stepOrder.indexOf(target) <= stepOrder.indexOf(step);

  const stepLabels = [
    { id: STEPS.SETUP, label: "1 · Setup" },
    { id: STEPS.SELECT, label: "2 · Scope" },
    { id: STEPS.AUDIT, label: "3 · Audit" },
    { id: STEPS.DASHBOARD, label: "4 · Dashboard" },
    { id: STEPS.FINDINGS, label: "5 · Findings" },
  ];

  return (
    <div className="app">
      {/* ── Header ── */}
      <header className="app-header">
        <div className="header-brand">
          <span className="header-logo">⚠</span>
          <div>
            <div className="header-title">OSHA General Industry Compliance Audit</div>
            <div className="header-sub">29 CFR 1910 · General Industry Standards</div>
          </div>
        </div>

        <nav className="header-nav">
          {stepLabels.map(({ id, label }) => (
            <button
              key={id}
              className={`nav-step ${step === id ? "active" : ""} ${isAccessible(id) ? "accessible" : "locked"}`}
              onClick={() => isAccessible(id) && setStep(id)}
              disabled={!isAccessible(id)}
            >
              {label}
            </button>
          ))}
          <button
            className="nav-step nav-manager"
            onClick={() => setShowManager(true)}
            title="Save / Load / New Audit"
          >
            {isDirty ? "💾 ●" : "💾"} Audits
          </button>
        </nav>
      </header>

      {/* ── Main ── */}
      <main className="app-main">
        {step === STEPS.SETUP && (
          <AuditSetup auditInfo={auditInfo} onComplete={handleSetupComplete} />
        )}
        {step === STEPS.SELECT && (
          <CategorySelector
            categories={AUDIT_CATEGORIES}
            selected={selectedCategories}
            onComplete={handleCategorySelection}
            onBack={() => setStep(STEPS.SETUP)}
          />
        )}
        {step === STEPS.AUDIT && (
          <AuditForm
            categories={selectedCriteria}
            responses={responses}
            onChange={handleResponseChange}
            onComplete={() => setStep(STEPS.DASHBOARD)}
            onBack={() => setStep(STEPS.SELECT)}
          />
        )}
        {step === STEPS.DASHBOARD && (
          <Dashboard
            auditInfo={auditInfo}
            categories={selectedCriteria}
            responses={responses}
            onViewFindings={() => setStep(STEPS.FINDINGS)}
            onBack={() => setStep(STEPS.AUDIT)}
          />
        )}
        {step === STEPS.FINDINGS && (
          <FindingsReport
            auditInfo={auditInfo}
            categories={selectedCriteria}
            responses={responses}
            onChange={handleResponseChange}
            onBack={() => setStep(STEPS.DASHBOARD)}
            onSave={handleSave}
            onNew={() => setShowManager(true)}
          />
        )}
      </main>

      {/* ── Footer ── */}
      <footer className="app-footer">
        <span>OSHA General Industry Audit Tool · 29 CFR 1910 · For professional EHS consulting use</span>
        <span>Not a substitute for legal counsel or official OSHA guidance</span>
      </footer>

      {/* ── Audit Manager Modal ── */}
      {showManager && (
        <AuditManager
          currentAuditId={currentAuditId}
          auditInfo={auditInfo}
          hasUnsavedChanges={isDirty}
          onSave={handleSave}
          onLoad={handleLoad}
          onNew={handleNew}
          onClose={() => setShowManager(false)}
        />
      )}
    </div>
  );
}
